import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { authService } from "api/services/auth.service";
import "./LoginPage.scss";

// 로그인 401 연속 실패 카운터 (모듈 레벨 — 페이지 마운트/언마운트와 무관하게 유지)
let loginFailCount = 0;
const MAX_LOGIN_FAILURES = 3;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ userId?: string; password?: string }>({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);

  const validate = () => {
    const errs: { userId?: string; password?: string } = {};
    if (!userId) errs.userId = "아이디를 입력해주세요.";
    if (!password) errs.password = "비밀번호를 입력해주세요.";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return;

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      await authService.login({ user_id: userId, password });
      loginFailCount = 0;
      navigate("/");
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string; detail?: string } };
        message?: string;
      };
      const status = axiosErr?.response?.status;

      // 401 실패 카운터 증가
      if (status === 401) {
        loginFailCount += 1;
        if (loginFailCount >= MAX_LOGIN_FAILURES) {
          authService.logout();
          loginFailCount = 0;
          setLocked(true);
          setApiError("로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.");
          setLoading(false);
          return;
        }
      }

      const msg = axiosErr?.message?.startsWith("관리자") ? axiosErr.message : (axiosErr?.response?.data?.message ?? axiosErr?.response?.data?.detail ?? "아이디 또는 비밀번호가 올바르지 않습니다.");
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__bg">
        <div className="login-page__bg-overlay" />
        <div className="login-page__heading">
          <h1>
            BUCKETLIST CLASS
            <br />
            admin
          </h1>
        </div>
      </div>

      <div className="login-page__form-area">
        <div className="login-card">
          <div className="login-card__header">
            <span className="login-card__logo">🔒</span>
            <h2>관리자 로그인</h2>
            <p>등록된 관리자 계정으로 로그인해주세요.</p>
          </div>

          <form className="login-card__form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label>아이디</label>
              <input
                type="text"
                autoComplete="username"
                placeholder="아이디 입력"
                value={userId}
                disabled={locked}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setErrors((p) => ({ ...p, userId: undefined }));
                  setApiError("");
                }}
                className={errors.userId ? "error" : ""}
              />
              {errors.userId && <span className="form-error">{errors.userId}</span>}
            </div>

            <div className="form-group">
              <label>비밀번호</label>
              <input
                type="password"
                autoComplete="current-password"
                placeholder="비밀번호 입력"
                value={password}
                disabled={locked}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((p) => ({ ...p, password: undefined }));
                  setApiError("");
                }}
                className={errors.password ? "error" : ""}
              />
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>

            {apiError && (
              <p className="form-error" style={{ marginTop: -8 }}>
                {apiError}
              </p>
            )}

            <button type="submit" className="login-card__submit" disabled={loading || locked}>
              {loading ? "로그인 중..." : "로그인"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
